import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

/**
 * Athlete-side metrics: PMC daily series + 28-day zone distribution.
 *
 * Both endpoints carry no PII and are pure read-only roll-ups, so
 * staleTime is generous (5 min — the underlying inputs change on
 * workout sync, which already invalidates dashboard).
 */

export interface PmcSeries {
  dates: string[];
  ctl: number[];
  atl: number[];
  tsb: number[];
  /** Каноническая серия ACWR с бэка (та же математика, что чип «Оптимум»). */
  acwr?: (number | null)[];
  delta_ctl_7d: number | null;
  delta_atl_7d: number | null;
  delta_tsb_7d: number | null;
}

export interface ZonesBuckets {
  low: number;
  mid: number;
  high: number;
}

export interface ZonesDistribution {
  percents: number[];
  buckets: ZonesBuckets;
  polarization: string;
  total_minutes: number;
  workouts: number;
}

export interface HrvBaseline {
  values: number[];
  dates: string[];
  baseline: number | null;
  baseline_sd: number | null;
  delta: number | null;
  in_band: boolean | null;
  samples: number;
}

export function useHrvBaseline(days: number = 14) {
  return useQuery<HrvBaseline>({
    queryKey: ["athlete-hrv-baseline", days],
    queryFn: async () => {
      const r = await api.get<HrvBaseline>("/athlete/metrics/hrv", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function usePmcSeries(weeks: number = 6) {
  return useQuery<PmcSeries>({
    queryKey: ["athlete-pmc-series", weeks],
    queryFn: async () => {
      const r = await api.get<PmcSeries>("/athlete/metrics/pmc/series", {
        params: { weeks },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ── Plan conflict scan (WAVE 2.2) ────────────────────────────

export interface PlanConflictSessionInput {
  weekday: number;
  tss: number;
  zone?: string | null;
  discipline?: string | null;
  title?: string | null;
}

export interface PlanConflictOut {
  key: string;
  severity: "watch" | "alert";
  title: string;
  summary: string;
  affected_days: number[];
}

export interface PlanConflictScanBody {
  sessions: PlanConflictSessionInput[];
  chronic_load?: number;
  days_to_race?: number | null;
  lang?: string;
}

export async function scanPlanConflicts(
  body: PlanConflictScanBody,
): Promise<PlanConflictOut[]> {
  const r = await api.post<PlanConflictOut[]>("/athlete/metrics/plan/conflicts", body);
  return r.data;
}

export function usePlanConflicts(body: PlanConflictScanBody, enabled = true) {
  return useQuery<PlanConflictOut[]>({
    queryKey: ["plan-conflicts", body.sessions.length, body.chronic_load, body.days_to_race, body.lang],
    queryFn: () => scanPlanConflicts(body),
    enabled: enabled && body.sessions.length > 0,
    staleTime: 60_000,
  });
}

// ── Power summary (PHASE 7 live wiring) ─────────────────────

export interface PowerSummary {
  ftp_watts: number | null;
  weeks: number;
  avg_np_watts: number | null;
  avg_intensity_factor: number | null;
  max_avg_power_watts: number | null;
  max_power_watts: number | null;
  rides_with_power: number;
  ftp_series: number[];
  week_labels: string[];
  // H2.8 v154 PART2 — avg HR across rides that carry BOTH power AND HR.
  // Lets ef-tracker lens show EF = avg_np_watts / avg_hr_bpm.
  avg_hr_bpm: number | null;
  rides_with_power_and_hr: number;
}

export function usePowerSummary(weeks: number = 12) {
  return useQuery<PowerSummary>({
    queryKey: ["athlete-power-summary", weeks],
    queryFn: async () => {
      const r = await api.get<PowerSummary>("/athlete/metrics/power/summary", {
        params: { weeks },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ── Race forecast (PHASE 7 live wiring — pacing-predictor) ──

export interface RaceForecast {
  supported: boolean;
  goal_id: number | null;
  race_date: string | null;
  days_to_race: number | null;
  race_distance: string | null;
  ctl_current: number | null;
  ctl_target: number | null;
  ctl_projected: number | null;
  readiness_pct: number | null;
  on_track: boolean;
  /** Утверждается ли вердикт вообще. false дальше 28 дней до старта:
   *  там измеренная доля ложных «успеешь» доходит до 37%. */
  on_track_known: boolean;
  taper_start_date: string | null;
  recommendation: string;
  recommendation_code: string | null;
  recommendation_params: Record<string, string | number>;
  alerts: Array<{ alert_type: string; severity: string }>;
}

export function useRaceForecast() {
  return useQuery<RaceForecast>({
    queryKey: ["athlete-race-forecast"],
    queryFn: async () => {
      const r = await api.get<RaceForecast>("/athlete/race-forecast");
      return r.data;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// ── Sensor coverage (PHASE 7 live wiring — data-quality lens) ──
//
// Endpoint: GET /athlete/metrics/data-quality?days=N
// Concern: how much SENSOR DATA do recent completed workouts carry?
//   • power_coverage_pct / hr_coverage_pct / gps_coverage_pct
//   • Used by analytics data-quality lens (LiveLensPage)
//
// NOT to be confused with the ACCOUNT-completeness endpoint at
// /athlete/me/data-quality (see dataQualityApi.ts → useDataQuality),
// which surfaces 5 onboarding flags + actionable hints for Settings.

export interface SensorCoverage {
  days: number;
  total_workouts: number;
  power_coverage_pct: number;
  hr_coverage_pct: number;
  gps_coverage_pct: number;
  has_streams_pct: number;
  /** Доля мощности С ДАТЧИКА среди тренировок с мощностью; null — источник не сказал. */
  power_from_device_pct?: number | null;
}

export function useSensorCoverage(days: number = 30) {
  return useQuery<SensorCoverage>({
    queryKey: ["athlete-sensor-coverage", days],
    queryFn: async () => {
      const r = await api.get<SensorCoverage>("/athlete/metrics/data-quality", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ── Run dynamics cadence (PHASE 7 live wiring — run-dynamics lens) ──

export interface RunDynamics {
  runs_with_cadence: number;
  days: number;
  cadence_avg_spm: number | null;
  cadence_latest_spm: number | null;
  cadence_delta: number | null;
  series: number[];
  economy_avg: number | null;         // aerobic efficiency: m·min⁻¹ per bpm (higher = better)
  economy_delta: number | null;
  runs_with_economy: number;
  economy_series: number[];
}

export function useRunDynamics(days: number = 84) {
  return useQuery<RunDynamics>({
    queryKey: ["athlete-run-dynamics", days],
    queryFn: async () => {
      const r = await api.get<RunDynamics>("/athlete/metrics/run-dynamics", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ── OWS path deviation (PHASE 7 live wiring — ows-correction lens) ──

export interface OwsCorrection {
  swims_with_gps: number;
  avg_deviation_pct: number | null;
  last_deviation_pct: number | null;
  series: number[];
}

export function useOwsCorrection(days: number = 180) {
  return useQuery<OwsCorrection>({
    queryKey: ["athlete-ows-correction", days],
    queryFn: async () => {
      const r = await api.get<OwsCorrection>("/athlete/metrics/ows-correction", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ── VO2 / VLamax (PHASE 7 live wiring — vo2-vlamax lens) ──

export interface Vo2Vlamax {
  tests_with_vo2: number;
  vo2max_latest: number | null;
  vo2max_first: number | null;
  vo2max_delta: number | null;
  vlamax_latest: number | null;
  vvo2max_kmh: number | null;             // velocity at VO2max (Billat)
  vvo2max_pace_s_per_km: number | null;
  series: number[];
  dates: string[];
}

export function useVo2Vlamax() {
  return useQuery<Vo2Vlamax>({
    queryKey: ["athlete-vo2-vlamax"],
    queryFn: async () => {
      const r = await api.get<Vo2Vlamax>("/athlete/metrics/vo2-vlamax");
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ── Triathlon transitions (PHASE 7 live wiring — transition lens) ──

export interface Transition {
  races_with_transitions: number;
  t1_avg_seconds: number | null;
  t2_avg_seconds: number | null;
  t1_best_seconds: number | null;
  t2_best_seconds: number | null;
  series: number[];
}

export function useTransition() {
  return useQuery<Transition>({
    queryKey: ["athlete-transition"],
    queryFn: async () => {
      const r = await api.get<Transition>("/athlete/metrics/transition");
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ── Aerobic decoupling (PHASE 7 live wiring — decoupling lens) ──

export interface Decoupling {
  days: number;
  total_with_streams: number;
  long_enough_workouts: number;
  avg_decoupling_pct: number | null;
  aerobic_pct: number | null;
  above_threshold_count: number;
  series: number[];
  dates: string[];
}

export function useDecoupling(days: number = 60, opts?: { enabled?: boolean }) {
  return useQuery<Decoupling>({
    enabled: opts?.enabled ?? true,
    queryKey: ["athlete-decoupling", days],
    queryFn: async () => {
      const r = await api.get<Decoupling>("/athlete/metrics/decoupling", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useZonesDistribution(days: number = 28) {
  return useQuery<ZonesDistribution>({
    queryKey: ["athlete-zones-distribution", days],
    queryFn: async () => {
      const r = await api.get<ZonesDistribution>("/athlete/metrics/zones/distribution", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export interface SeasonStats {
  year: number;
  year_start: string;
  swim_km: number;
  bike_km: number;
  run_km: number;
  other_km: number;
  total_hours: number;
  races: number;
  prs: number;
  sessions: number;
}

/**
 * Canon раздел ScreenProfile СЕЗОН ИТОГО — per-year aggregate stats endpoint.
 * Source: GET /athlete/analytics/season?year={YYYY} (введён в v153 P3).
 */
export function useSeasonStats(year?: number) {
  return useQuery<SeasonStats>({
    queryKey: ["athlete-season-stats", year ?? null],
    queryFn: async () => {
      const r = await api.get<SeasonStats>("/athlete/analytics/season", {
        params: year != null ? { year } : {},
      });
      return r.data;
    },
    staleTime: 15 * 60_000,
  });
}

// ── Career peaks (multi-year) — personal bests across seasons ──

export interface CareerYearPeak {
  year: number;
  peak_ctl: number | null;
  /** Откуда пик: "storica" — наш движок, "intervals" — импорт. */
  ctl_source?: string | null;
  ctl_days?: number | null;
  /** Наблюдали меньше половины года — «пик» может быть пиком фрагмента. */
  ctl_partial?: boolean | null;
  best_vo2max: number | null;
  best_power_watts: number | null;
  best_css_sec_per_100m: number | null;
  best_vdot: number | null;
  total_hours: number;
  races: number;
}

export interface CareerPeaks {
  years: CareerYearPeak[];
  alltime_peak_ctl: number | null;
  alltime_peak_ctl_date: string | null;
  alltime_best_vo2max: number | null;
  alltime_best_power_watts: number | null;
  alltime_best_css_sec_per_100m: number | null;
}

/** Multi-year personal peaks — GET /athlete/analytics/career-peaks (2026-07-15). */
export function useCareerPeaks() {
  return useQuery<CareerPeaks>({
    queryKey: ["athlete-career-peaks"],
    queryFn: async () => {
      const r = await api.get<CareerPeaks>("/athlete/analytics/career-peaks");
      return r.data;
    },
    staleTime: 30 * 60_000,
  });
}

// ── Пиллар B / C / D — individualized physiology + energy availability ──

export interface CriticalPower {
  available: boolean;
  cp_watts: number | null;
  w_prime_j: number | null;
  r_squared: number | null;
  rides_analyzed: number;
  curve: Array<{ duration_s: number; best_power_w: number }>;
  power_zones?: Array<{ n: number; min_watts: number; max_watts: number }>;
}

export function useCriticalPower(days = 90) {
  return useQuery<CriticalPower>({
    queryKey: ["athlete-critical-power", days],
    queryFn: async () => {
      const r = await api.get<CriticalPower>("/athlete/metrics/critical-power", { params: { days } });
      return r.data;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export interface CriticalSpeed {
  available: boolean;
  cs_mps: number | null;
  cs_pace_s_per_km: number | null;
  d_prime_m: number | null;
  r_squared: number | null;
  runs_analyzed: number;
  curve: Array<{ duration_s: number; best_speed_mps: number }>;
  pace_zones?: Array<{ n: number; min_sec_per_km: number; max_sec_per_km: number }>;
}

export function useCriticalSpeed(days = 90) {
  return useQuery<CriticalSpeed>({
    queryKey: ["athlete-critical-speed", days],
    queryFn: async () => {
      const r = await api.get<CriticalSpeed>("/athlete/metrics/critical-speed", { params: { days } });
      return r.data;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export interface CriticalSwim {
  available: boolean;
  css_sec_per_100m: number | null;
  last_test_date: string | null;
  days_since_test: number | null;
  delta_30d: number | null;   // sec/100m vs 30d ago (negative = faster)
  delta_90d: number | null;
  stroke_rate_spm: number | null;
  swim_sessions: number;
  pace_zones?: Array<{ n: number; min_sec_per_100m: number; max_sec_per_100m: number }>;
}

export function useCriticalSwim() {
  return useQuery<CriticalSwim>({
    queryKey: ["athlete-critical-swim"],
    queryFn: async () => {
      const r = await api.get<CriticalSwim>("/athlete/metrics/critical-swim");
      return r.data;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export interface BikeDynamics {
  rides_with_cadence: number;
  days: number;
  cadence_avg_rpm: number | null;
  cadence_latest_rpm: number | null;
  cadence_delta: number | null;
  series: number[];
}

export function useBikeDynamics(days = 84) {
  return useQuery<BikeDynamics>({
    queryKey: ["athlete-bike-dynamics", days],
    queryFn: async () => {
      const r = await api.get<BikeDynamics>("/athlete/metrics/bike-dynamics", { params: { days } });
      return r.data;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export interface DurabilityTrend {
  rides_with_durability: number;
  trend: "improving" | "stable" | "worsening" | null;
  avg_ratio: number | null;
  series: Array<{ date: string; ratio: number; pct_drop: number; total_kj: number }>;
}

export function useDurabilityTrend(days = 120, opts?: { enabled?: boolean }) {
  return useQuery<DurabilityTrend>({
    enabled: opts?.enabled ?? true,
    queryKey: ["athlete-durability-trend", days],
    queryFn: async () => {
      const r = await api.get<DurabilityTrend>("/athlete/metrics/durability-trend", { params: { days } });
      return r.data;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

// Пиллар C — athlete-facing Low Energy Availability signal. STRUCTURED markers;
// the non-diagnostic disclaimer lives in the FE card copy (i18n), not here.
export interface EnergyAvailability {
  risk_level: "none" | "low" | "moderate" | "elevated";
  weight_change_pct_per_week: number | null;
  weight_loss_flag: boolean;
  autonomic_flag: boolean;
  high_load_flag: boolean;
  factors: string[];
}

export function useEnergyAvailability() {
  return useQuery<EnergyAvailability>({
    queryKey: ["athlete-energy-availability"],
    queryFn: async () => {
      const r = await api.get<EnergyAvailability>("/athlete/metrics/energy-availability");
      return r.data;
    },
    staleTime: 30 * 60_000,
    retry: false,
  });
}
